import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import ViewStreamRoundedIcon from "@mui/icons-material/ViewStreamRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";

import Dialog from "../../../components/dialog/Dialog";
import Content from "../../../components/Content";
import { GetUsers } from "../../../hooks/useUserData";
import { CreateUserModal } from "../../../modals/customer/CreateUserModal";
import { BulkImportUsersModal } from "../../../modals/customer/BulkImportUsersModal";
import { UserEditModal } from "./UserEditView";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const AVATAR_GRADIENTS = [
  ["#8a1125", "#c23b54"],
  ["#10324a", "#2d5a7e"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (first = "", last = "") => {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (!f && !l) return "··";
  if (f && l) return (f[0] + l[0]).toUpperCase();
  const only = f || l;
  return only.slice(0, 2).toUpperCase();
};

const fullName = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || "Namnlös användare";

const Avatar = ({ user, size = 46 }) => {
  const seed = `${user.first_name || ""} ${user.last_name || ""}`.trim() || String(user.id);
  const [a, b] = gradientFor(seed);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 10px 22px -12px ${a}55`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "14px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.22), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(user.first_name, user.last_name)}
      </Typography>
    </Box>
  );
};

const MetaChip = ({ icon: Icon, label, tone = "default" }) => {
  const styles =
    tone === "brand"
      ? {
          border: "rgba(138,17,37,0.32)",
          bg: "rgba(138,17,37,0.08)",
          fg: BRAND,
          dot: BRAND,
          dotHalo: "rgba(138,17,37,0.18)",
        }
      : tone === "navy"
      ? {
          border: "rgba(16,50,74,0.32)",
          bg: "rgba(16,50,74,0.08)",
          fg: NAVY,
          dot: NAVY,
          dotHalo: "rgba(16,50,74,0.18)",
        }
      : {
          border: "rgba(10,22,40,0.12)",
          bg: "rgba(10,22,40,0.025)",
          fg: "rgba(95,104,119,0.75)",
          dot: "#c7c3ba",
          dotHalo: "transparent",
        };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${styles.border}`,
        background: styles.bg,
        color: styles.fg,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.005em",
        fontFamily: '"DM Sans", sans-serif',
        maxWidth: 240,
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: styles.dot,
          boxShadow: `0 0 0 4px ${styles.dotHalo}`,
          flexShrink: 0,
        }}
      />
      {Icon && <Icon sx={{ fontSize: 16, opacity: 0.95, flexShrink: 0 }} />}
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

const MetricCell = ({ label, value, total, tint, index }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box
      sx={{
        py: 3.5,
        pr: 3,
        pl: { xs: 0, md: index === 0 ? 0 : 3 },
        position: "relative",
        borderRight: {
          xs: "none",
          md: index < 3 ? `1px solid ${LINE}` : "none",
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${LINE}` : "none",
          md: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: INK_MUTED,
          mb: 1.5,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: "2.75rem",
            fontWeight: 500,
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Typography>
        {total > 0 && index > 0 && (
          <Typography
            sx={{
              fontSize: "12px",
              color: INK_MUTED,
              fontWeight: 500,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            / {total}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "2px",
          width: "100%",
          maxWidth: 180,
          background: LINE_SOFT,
          borderRadius: "2px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: tint,
            transformOrigin: "left",
            transform: `scaleX(${pct / 100})`,
          }}
        />
      </Box>
    </Box>
  );
};

const UserRow = ({ user, onSelect, index }) => (
  <Box
    onClick={() => onSelect(user.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "auto 1fr auto",
        md: "auto minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr) auto",
      },
      alignItems: "center",
      gap: { xs: 2, md: 3 },
      px: 3,
      py: 2.25,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "16%",
        bottom: "16%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "& .row-name": {
          color: BRAND,
        },
      },
    }}
  >
    <Avatar user={user} size={46} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "16.5px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {fullName(user)}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.25,
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: "0.01em",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {user.email || "Ingen e-post"}
      </Typography>
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      {user.group_name ? (
        <MetaChip icon={GroupsRoundedIcon} label={user.group_name} tone="brand" />
      ) : (
        <Typography
          sx={{
            fontSize: "12px",
            color: INK_MUTED,
            fontStyle: "italic",
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          Ingen grupp
        </Typography>
      )}
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      {user.facility_name ? (
        <MetaChip icon={ApartmentRoundedIcon} label={user.facility_name} tone="navy" />
      ) : (
        <Typography
          sx={{
            fontSize: "12px",
            color: INK_MUTED,
            fontStyle: "italic",
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          Ingen anläggning
        </Typography>
      )}
    </Box>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const UserCard = ({ user, onSelect, index }) => (
  <Box
    onClick={() => onSelect(user.id)}
    sx={{
      position: "relative",
      p: 3.25,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "48px",
        height: "2px",
        background: BRAND,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: "rgba(10,22,40,0.18)",
        transform: "translateY(-3px)",
        boxShadow:
          "0 24px 48px -24px rgba(10,22,40,0.16), 0 2px 6px -2px rgba(10,22,40,0.04)",
        "&::before": { transform: "scaleX(1)" },
        "& .card-arrow": { transform: "translateX(3px)", color: BRAND },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        mb: 2.5,
      }}
    >
      <Avatar user={user} size={56} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "20px",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
        mb: 0.75,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        minHeight: "48px",
      }}
    >
      {fullName(user)}
    </Typography>
    <Stack spacing={0.75} sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <EmailRoundedIcon sx={{ fontSize: 13, color: INK_MUTED }} />
        <Typography
          sx={{
            fontSize: "12.5px",
            color: INK_MUTED,
            fontFamily: '"DM Sans", sans-serif',
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.email || "—"}
        </Typography>
      </Box>
      {user.phone && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhoneRoundedIcon sx={{ fontSize: 13, color: INK_MUTED }} />
          <Typography
            sx={{
              fontSize: "12.5px",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {user.phone}
          </Typography>
        </Box>
      )}
    </Stack>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
      {user.group_name && (
        <MetaChip icon={GroupsRoundedIcon} label={user.group_name} tone="brand" />
      )}
      {user.facility_name && (
        <MetaChip icon={ApartmentRoundedIcon} label={user.facility_name} tone="navy" />
      )}
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 2.25,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: INK_MUTED,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        Redigera
      </Typography>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 18,
          color: INK_MUTED,
          transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

const EmptyState = ({ onCreate, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <PersonRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga användare ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 420,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm eller rensa filtren för att se alla användare."
        : "Lägg till din första användare eller importera flera samtidigt via CSV-fil."}
    </Typography>
    {!hasQuery && (
      <Box
        component="button"
        type="button"
        onClick={onCreate}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": {
            background: "#6b0d1d",
            transform: "translateY(-1px)",
          },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        Lägg till användare
      </Box>
    )}
  </Box>
);

const selectSx = {
  height: 44,
  minWidth: 170,
  background: "#fff",
  fontSize: "13px",
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  fontWeight: 600,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "13px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.1,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

export const UserView = () => {
  const [organisationId, setOrganisationId] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  const { data: usersData, isLoading } = GetUsers();

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  const users = useMemo(() => {
    const raw = Array.isArray(usersData?.data) ? usersData.data : [];
    return raw.map((u) => ({
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      phone: u.phone,
      group_name: u.user_group?.group_name || "",
      facility_name: u.facility?.facility_name || "",
    }));
  }, [usersData]);

  const groupOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      if (u.group_name) set.add(u.group_name);
    });
    return Array.from(set).sort();
  }, [users]);

  const facilityOptions = useMemo(() => {
    const set = new Set();
    users.forEach((u) => {
      if (u.facility_name) set.add(u.facility_name);
    });
    return Array.from(set).sort();
  }, [users]);

  const metrics = useMemo(() => {
    const uniqueGroupNames = new Set();
    let withGroup = 0;
    let withFacility = 0;
    for (const u of users) {
      if (u.group_name) {
        withGroup += 1;
        uniqueGroupNames.add(u.group_name);
      }
      if (u.facility_name) withFacility += 1;
    }
    return {
      total: users.length,
      withGroup,
      withFacility,
      uniqueGroups: uniqueGroupNames.size,
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q) {
        const hay =
          `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""} ${
            u.phone || ""
          }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (groupFilter !== "all" && u.group_name !== groupFilter) return false;
      if (facilityFilter !== "all" && u.facility_name !== facilityFilter)
        return false;
      return true;
    });
  }, [users, search, groupFilter, facilityFilter]);

  const closeEditModal = () => setEditUserId(null);

  const hasActiveQuery =
    !!search.trim() || groupFilter !== "all" || facilityFilter !== "all";

  return (
    <Content>
      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 80,
            left: -140,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(16,50,74,0.07), rgba(16,50,74,0) 62%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            animation: `${floatOrb} 18s ease-in-out infinite reverse`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Organisation · Användarregister
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Användare
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 300,
                    fontStyle: "italic",
                    fontSize: "0.58em",
                    color: BRAND,
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  & medlemskap
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Hantera alla personer i din organisation. Tilldela grupper och
                anläggningar, importera medlemmar i bulk, eller justera
                åtkomstnivåer per individ.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setIsImportModalOpen(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2.5,
                  py: 1.625,
                  background: "#fff",
                  color: INK,
                  border: `1px solid ${LINE}`,
                  borderRadius: "12px",
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                  "&:hover": {
                    borderColor: "rgba(10,22,40,0.28)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 10px 24px -14px rgba(10,22,40,0.18)",
                  },
                }}
              >
                <FileUploadRoundedIcon sx={{ fontSize: 18 }} />
                Importera
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2.75,
                  py: 1.625,
                  background: INK,
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 600,
                  letterSpacing: "0.005em",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                  boxShadow:
                    "0 14px 32px -14px rgba(10,22,40,0.45), 0 2px 4px -2px rgba(10,22,40,0.08)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(135deg, ${BRAND}, transparent 70%)`,
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                  },
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 22px 44px -18px rgba(10,22,40,0.55), 0 2px 6px -2px rgba(10,22,40,0.12)",
                    "&::before": { opacity: 1 },
                  },
                  "& > *": { position: "relative", zIndex: 1 },
                }}
              >
                <AddRoundedIcon sx={{ fontSize: 18 }} />
                Ny användare
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              borderTop: `1px solid ${LINE}`,
              borderBottom: `1px solid ${LINE}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
              backdropFilter: "blur(4px)",
            }}
          >
            <MetricCell
              label="Totalt antal användare"
              value={metrics.total}
              total={metrics.total}
              tint={INK}
              index={0}
            />
            <MetricCell
              label="Med grupp tilldelad"
              value={metrics.withGroup}
              total={metrics.total}
              tint={BRAND}
              index={1}
            />
            <MetricCell
              label="Med anläggning"
              value={metrics.withFacility}
              total={metrics.total}
              tint={NAVY}
              index={2}
            />
            <MetricCell
              label="Unika grupper"
              value={metrics.uniqueGroups}
              total={metrics.total}
              tint="#2b8c4d"
              index={3}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              height: 44,
              minWidth: 260,
              flex: { xs: "1 1 100%", md: "0 1 320px" },
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              transition: "all 0.25s ease",
              "&:focus-within": {
                borderColor: "rgba(10,22,40,0.28)",
                boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök namn, e-post eller telefon..."
              sx={{
                flex: 1,
                fontSize: "14px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": {
                  color: INK_MUTED,
                  opacity: 1,
                },
              }}
            />
          </Box>

          <FormControl>
            <Select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(v) => (
                <>
                  <GroupsRoundedIcon sx={{ fontSize: 15, color: INK_MUTED }} />
                  {v === "all" ? "Alla grupper" : v}
                </>
              )}
              sx={selectSx}
            >
              <MenuItem value="all">Alla grupper</MenuItem>
              {groupOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <Select
              value={facilityFilter}
              onChange={(e) => setFacilityFilter(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(v) => (
                <>
                  <ApartmentRoundedIcon sx={{ fontSize: 15, color: INK_MUTED }} />
                  {v === "all" ? "Alla anläggningar" : v}
                </>
              )}
              sx={selectSx}
            >
              <MenuItem value="all">Alla anläggningar</MenuItem>
              {facilityOptions.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                px: 1.5,
                py: 0.75,
                color: INK_MUTED,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: INK,
                  color: "#fff",
                  "&:hover": { background: INK },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewStreamRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <FilterAltRoundedIcon sx={{ fontSize: 14, color: INK_MUTED }} />
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filteredUsers.length} användare${
                  hasActiveQuery ? ` av ${users.length}` : ""
                }`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom: i < 4 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={46}
                    height={46}
                    sx={{ borderRadius: "14px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="32%" height={20} />
                    <Skeleton width="48%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                  <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: "999px" }} />
                  <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: "999px" }} />
                </Box>
              ))}
            </Box>
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              onCreate={() => setIsCreateModalOpen(true)}
              hasQuery={hasActiveQuery}
            />
          ) : viewMode === "list" ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filteredUsers.map((user, i) => (
                <UserRow
                  key={user.id}
                  user={user}
                  onSelect={setEditUserId}
                  index={i}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {filteredUsers.map((user, i) => (
                <UserCard
                  key={user.id}
                  user={user}
                  onSelect={setEditUserId}
                  index={i}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        title="Lägg till användare"
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <CreateUserModal
          customerid={organisationId}
          closeCreateModal={() => setIsCreateModalOpen(false)}
          showRoleSelect
        />
      </Dialog>
      <Dialog
        title="Importera användare från CSV"
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        maxWidth="md"
      >
        <BulkImportUsersModal closeModal={() => setIsImportModalOpen(false)} />
      </Dialog>
      {editUserId && (
        <Dialog
          title="Redigera användare"
          open={!!editUserId}
          onClose={closeEditModal}
        >
          <UserEditModal
            userId={editUserId}
            onClose={closeEditModal}
            showRoleSelect
          />
        </Dialog>
      )}
    </Content>
  );
};

export default UserView;
